import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import VariantPricingModal from '../components/VariantPricingModal';
import EditProductModal from '../components/EditProductModal';
import EditVariantModal, { VARIANT_API, variantHeaders } from '../components/EditVariantModal';
import { ArrowLeft, Package, RefreshCw, DollarSign, Edit, RotateCw, Plus } from 'lucide-react';

const ProductDetail = ({ user, onLogout }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [brand, setBrand] = useState(null);
  const [variants, setVariants] = useState([]);
  const [editingVariantId, setEditingVariantId] = useState(null);
  const [variantError, setVariantError] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshingPrice, setRefreshingPrice] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const [showAllSpecs, setShowAllSpecs] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [hasMissingPrices, setHasMissingPrices] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showAddVariantModal, setShowAddVariantModal] = useState(false);
  const [addingVariant, setAddingVariant] = useState(false);
  const [newVariant, setNewVariant] = useState({
    memory: '8GB/128GB',
    color: 'Black',
    sales_grade: 'GOOD',
    stock_quantity: 10,
    sale_price: '',
    mrp: ''
  });

  useEffect(() => {
    fetchProductDetails();
  }, [id]);

  const handleRefreshPrice = async () => {
    try {
      setRefreshing(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/products/${id}/refresh-flipkart-price`,
        {
          method: 'POST',
          headers: {
            ...variantHeaders()
          }
        }
      );

      const data = await response.json();

      if (response.ok && data.mrp && data.flipkart_price) {
        alert(`✓ Price refreshed!\nMRP: ₹${data.mrp.toLocaleString()}\nFSP: ₹${data.flipkart_price.toLocaleString()}`);
        fetchProductDetails(); // Reload data
      } else {
        alert('⚠️ ' + (data.message || 'Unable to fetch price from Flipkart'));
      }
    } catch (error) {
      console.error('Error refreshing price:', error);
      alert('Failed to refresh price');
    } finally {
      setRefreshing(false);
    }
  };

  const fetchProductDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api/products/${id}`,
        {
          headers: {
            ...variantHeaders()
          }
        }
      );
      const data = await response.json();
      
      setProduct(data.product);
      setBrand(data.brand);
      setVariants(data.variants || []);
      setVariantError('');
      try {
        const variantResponse = await fetch(`${VARIANT_API}?product_id=${encodeURIComponent(id)}`, { headers: variantHeaders() });
        const variantData = await variantResponse.json();
        if (!variantResponse.ok) throw new Error(typeof variantData.detail === 'string' ? variantData.detail : 'Could not load live variant stock');
        setVariants(variantData.variants || []);
      } catch (error) { setVariantError(error.message); }

      // Check if any variants are missing prices
      const missingPrices = (data.variants || []).some(v => !v.mrp || !v.fsp);
      setHasMissingPrices(missingPrices);
      
      // Auto-show pricing modal if prices are missing
      if (missingPrices) {
        setShowPricingModal(true);
      }
    } catch (error) {
      console.error('Error fetching product details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshFSP = async () => {
    try {
      setRefreshingPrice(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/products/${id}/refresh-flipkart-price`,
        {
          method: 'POST',
          headers: {
            ...variantHeaders()
          }
        }
      );

      const data = await response.json();

      if (response.ok) {
        alert(`FSP updated!\nOld: ₹${product.flipkart_price?.toLocaleString()}\nNew: ₹${data.flipkart_price?.toLocaleString()}`);
        fetchProductDetails();
      } else {
        alert(data.detail || 'Failed to refresh price');
      }
    } catch (error) {
      console.error('Error refreshing price:', error);
      alert('Failed to refresh price');
    } finally {
      setRefreshingPrice(false);
    }
  };

  const handleCreateVariant = async (e) => {
    e.preventDefault();
    try {
      setAddingVariant(true);
      const payload = {
        sku_id: id,
        memory: newVariant.memory || 'Standard',
        color: newVariant.color || 'Default',
        sales_grade: newVariant.sales_grade || 'GOOD',
        sales_category: 'ZOPRO',
        stock_quantity: Number(newVariant.stock_quantity) || 0,
        sale_price: newVariant.sale_price ? Number(newVariant.sale_price) : Number(product.mrp || 0),
        mrp: newVariant.mrp ? Number(newVariant.mrp) : Number(product.mrp || 0)
      };
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api/products/${id}/variants`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...variantHeaders()
          },
          body: JSON.stringify(payload)
        }
      );
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to create variant');
      }
      setShowAddVariantModal(false);
      fetchProductDetails();
      alert('✓ Variant and initial stock added successfully!');
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setAddingVariant(false);
    }
  };

  const handleToggleLiveStatus = async () => {
    try {
      setLoading(true);
      const newStatus = !product.is_active;
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api/products/${id}/status`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...variantHeaders()
          },
          body: JSON.stringify({ is_active: newStatus })
        }
      );
      const data = await response.json();
      if (response.ok) {
        alert(`✓ Product ${newStatus ? 'is now LIVE on website!' : 'is now OFFLINE (hidden)'}`);
        fetchProductDetails();
      } else {
        alert('Failed: ' + (data.detail || 'Could not update status'));
      }
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update status');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  if (!product) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="p-6">
          <div className="text-center py-12">
            <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">Product not found</h3>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Modals */}
        {editingVariantId && <EditVariantModal variantId={editingVariantId} onClose={() => setEditingVariantId(null)} onSaved={(updated) => setVariants((current) => current.map((item) => item.id === updated.id ? { ...item, ...updated } : item))} />}
        <VariantPricingModal
          isOpen={showPricingModal}
          onClose={() => setShowPricingModal(false)}
          product={product}
          variants={variants}
          onSuccess={fetchProductDetails}
        />

        <EditProductModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          product={product}
          onSuccess={fetchProductDetails}
        />

        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/products')}
              className="p-2 hover:bg-muted rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-3xl font-bold text-foreground">{product.model_name}</h1>
                <span className={`text-xs px-3 py-1 rounded ${
                  product.is_active
                    ? 'bg-success/10 text-success'
                    : 'bg-muted text-muted-foreground'
                }`}>
                  {product.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <p className="text-muted-foreground">SKU: {product.sku_code}</p>
              {product.live_date && (
                <p className="text-sm text-muted-foreground">
                  Live since: {new Date(product.live_date).toLocaleString()}
                </p>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            {/* Go Live / Offline Toggle */}
            <button
              onClick={handleToggleLiveStatus}
              className={`flex items-center gap-2 px-4 py-2 font-medium rounded-lg transition-colors ${
                product.is_active
                  ? 'bg-amber-100 text-amber-800 hover:bg-amber-200 border border-amber-300'
                  : 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm'
              }`}
            >
              {product.is_active ? '⏸ Take Offline' : '🟢 Go Live'}
            </button>

            {/* Edit Product Button */}
            <button
              onClick={() => setShowEditModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/90 transition-colors"
            >
              <Edit className="w-4 h-4" />
              Edit
            </button>

            {/* Refresh Price Button */}
            <button
              onClick={handleRefreshPrice}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 bg-info text-info-foreground rounded-lg hover:bg-info/90 transition-colors disabled:opacity-50"
            >
              <RotateCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh Price
            </button>

            {/* Set/Update Pricing Button */}
            <button
              onClick={() => setShowPricingModal(true)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                hasMissingPrices
                  ? 'bg-warning text-warning-foreground hover:bg-warning/90'
                  : 'bg-primary text-primary-foreground hover:bg-primary/90'
              }`}
            >
              <DollarSign className="w-4 h-4" />
              {hasMissingPrices ? 'Set Pricing' : 'Update Pricing'}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Product Info */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow p-6">
              {/* Image Gallery */}
              {product.images && product.images.length > 0 ? (
                <div className="mb-6">
                  {/* Main Image */}
                  <div className="mb-3 bg-gray-50 rounded-lg p-4 flex items-center justify-center" style={{ minHeight: '300px' }}>
                    <img
                      src={product.images[selectedImage]}
                      alt={`${product.model_name} - View ${selectedImage + 1}`}
                      className="max-w-full max-h-80 object-contain"
                    />
                  </div>
                  
                  {/* Thumbnail Gallery */}
                  {product.images.length > 1 && (
                    <div className="grid grid-cols-4 gap-2">
                      {product.images.map((image, idx) => (
                        <button
                          key={idx}
                          onClick={() => setSelectedImage(idx)}
                          className={`border-2 rounded-lg p-2 transition-all ${
                            selectedImage === idx
                              ? 'border-[#2b3380] bg-[#2b338015]'
                              : 'border-gray-200 hover:border-gray-400'
                          }`}
                        >
                          <img
                            src={image}
                            alt={`Thumbnail ${idx + 1}`}
                            className="w-full h-16 object-contain"
                          />
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {/* Image Counter */}
                  <div className="text-center mt-2 text-sm text-gray-500">
                    {selectedImage + 1} / {product.images.length}
                  </div>
                </div>
              ) : (
                <div className="mb-6 bg-gray-100 h-80 rounded-lg flex items-center justify-center">
                  <div className="text-center">
                    <Package className="w-16 h-16 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">No images available</p>
                  </div>
                </div>
              )}

              {/* Brand & Category */}
              <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Brand</h3>
                <p className="text-lg font-semibold">{brand?.name || 'N/A'}</p>
              </div>

              <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Category</h3>
                <p className="text-lg">{product.category}</p>
              </div>

              {/* HSN Code */}
              {product.hsn_code && (
                <div className="mb-4">
                  <h3 className="text-sm font-medium text-gray-500 mb-2">HSN Code</h3>
                  <p className="text-lg font-mono">{product.hsn_code}</p>
                </div>
              )}

              {/* Pricing Info */}
              {product.mrp && (
                <div className="mb-4">
                  <h3 className="text-sm font-medium text-gray-500 mb-2">MRP (Original)</h3>
                  <p className="text-lg font-semibold text-gray-900">
                    ₹{product.mrp.toLocaleString()}
                  </p>
                </div>
              )}

              {product.flipkart_price && (
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-gray-500">FSP (Flipkart Selling Price)</h3>
                    <button
                      onClick={handleRefreshFSP}
                      disabled={refreshingPrice}
                      className="p-1 text-[#2b3380] hover:text-[#1f2660] disabled:opacity-50"
                      title="Update Price"
                    >
                      <RefreshCw className={`w-4 h-4 ${refreshingPrice ? 'animate-spin' : ''}`} />
                    </button>
                  </div>
                  <p className="text-lg font-semibold text-green-600">
                    ₹{product.flipkart_price.toLocaleString()}
                  </p>
                  {product.flipkart_last_scraped && (
                    <p className="text-xs text-gray-400 mt-1">
                      Last updated: {new Date(product.flipkart_last_scraped).toLocaleDateString()}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Specifications & Variants */}
          <div className="lg:col-span-2 space-y-6">
            {/* Key Points */}
            {product.key_points && Object.keys(product.key_points).length > 0 && (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold mb-4 text-gray-900">Key Points</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {Object.entries(product.key_points).map(([key, value]) => {
                    if (!value) return null;
                    return (
                      <div key={key} className="bg-white rounded-lg p-3 shadow-sm">
                        <dt className="text-xs font-medium text-gray-500 uppercase mb-1">
                          {key.replace(/_/g, ' ')}
                        </dt>
                        <dd className="text-sm font-semibold text-gray-900">{value}</dd>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Detailed Specifications */}
            {product.specifications && Object.keys(product.specifications).length > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold">Detailed Specifications</h2>
                  <button
                    onClick={() => setShowAllSpecs(!showAllSpecs)}
                    className="text-sm text-[#2b3380] hover:text-[#1f2660] font-medium"
                  >
                    {showAllSpecs ? 'Show Less' : 'Show More'}
                  </button>
                </div>
                
                <div className="space-y-6">
                  {Object.entries(product.specifications).map(([sectionKey, sectionValue], index) => {
                    if (!sectionValue || (typeof sectionValue === 'object' && Object.keys(sectionValue).length === 0)) return null;
                    
                    // Key sections to always show (first 5)
                    const keySpecSections = ['Network', 'Launch', 'Body', 'Display', 'Platform', 'Memory'];
                    const isKeySection = keySpecSections.includes(sectionKey);
                    
                    // Hide non-key sections when collapsed
                    if (!showAllSpecs && !isKeySection && index >= 6) {
                      return null;
                    }
                    
                    // Handle nested objects (section with specs)
                    const renderSection = () => {
                      if (typeof sectionValue === 'object' && sectionValue !== null) {
                        return (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {Object.entries(sectionValue).map(([specKey, specValue]) => {
                              if (!specValue || specValue === '-') return null;
                              
                              return (
                                <div key={specKey} className="flex flex-col">
                                  <dt className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
                                    {specKey}
                                  </dt>
                                  <dd className="text-sm text-gray-900 whitespace-pre-wrap">
                                    {specValue}
                                  </dd>
                                </div>
                              );
                            })}
                          </div>
                        );
                      }
                      return <span className="text-sm text-gray-900 whitespace-pre-wrap">{sectionValue}</span>;
                    };
                    
                    return (
                      <div key={sectionKey} className="border-b border-gray-200 pb-4 last:border-b-0">
                        <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                          <span className="w-1 h-5 bg-[#2b3380] mr-2"></span>
                          {sectionKey}
                        </h3>
                        {renderSection()}
                      </div>
                    );
                  })}
                </div>
                
                {/* Show collapsed message */}
                {!showAllSpecs && Object.keys(product.specifications).length > 6 && (
                  <div className="mt-4 text-center">
                    <p className="text-sm text-gray-500 mb-2">
                      {Object.keys(product.specifications).length - 6} more sections available
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Variants (ZUPC) */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h2 className="text-xl font-semibold">Product Variants &amp; Stock (ZUPC)</h2>
                  <p className="text-xs text-gray-500 mt-0.5">Manage stock, grades, and prices for this product's variants</p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowAddVariantModal(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-[#2b3380] hover:bg-[#1e2669] text-white rounded-lg text-xs font-semibold shadow-sm transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  + Add Variant / Stock
                </button>
              </div>

              {variantError && <p role="alert" className="mb-3 text-red-700">{variantError}</p>}
              {variants.length === 0 ? (
                <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg bg-gray-50">
                  <Package className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                  <p className="font-semibold text-gray-700">No variants created yet.</p>
                  <p className="text-xs text-gray-500 mt-1 mb-4">Add your first variant to set live stock and selling rate for this product.</p>
                  <button
                    type="button"
                    onClick={() => setShowAddVariantModal(true)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#2b3380] hover:bg-[#1e2669] text-white rounded-lg text-sm font-semibold shadow transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    + Add Variant &amp; Stock Now
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {variants.map(variant => (
                    <div
                      key={variant.id}
                      className="border rounded-lg p-4 hover:shadow-md transition-shadow bg-white"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="mb-3">
                            <h3 className="font-semibold text-gray-900 text-lg">{variant.zupc_code}</h3>
                            <p className="text-sm text-gray-600 mt-1">
                              Grade: <strong>{variant.sales_grade || 'Not set'}</strong> · 
                              Available Stock: <strong className="text-green-700">{variant.stock_quantity ?? 0} units</strong> · 
                              Rate: <strong>₹{(variant.sale_price || variant.selling_price || product.mrp || 0).toLocaleString()}</strong>
                            </p>
                            <button type="button" onClick={() => setEditingVariantId(variant.id)} className="mt-2 border border-[#2b3380] text-[#2b3380] hover:bg-indigo-50 rounded px-3 py-1 text-xs font-semibold">
                              Edit stock &amp; grade
                            </button>
                          </div>

                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div className="flex items-center gap-2">
                              <span className="text-gray-500">Memory:</span>
                              <span className="font-medium text-gray-900">{variant.memory || `${variant.storage}`}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-gray-500">Color:</span>
                              <span className="font-medium text-gray-900">{variant.color}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Add Variant & Stock Modal */}
        {showAddVariantModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
              <div className="flex justify-between items-center mb-4 border-b pb-3">
                <h3 className="text-lg font-bold text-gray-900">+ Add Variant &amp; Initial Stock</h3>
                <button
                  type="button"
                  onClick={() => setShowAddVariantModal(false)}
                  className="text-gray-400 hover:text-gray-600 font-bold"
                >
                  ✕
                </button>
              </div>
              <form onSubmit={handleCreateVariant} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Memory / Storage *</label>
                  <input
                    type="text"
                    required
                    value={newVariant.memory}
                    onChange={(e) => setNewVariant((curr) => ({ ...curr, memory: e.target.value }))}
                    placeholder="e.g. 6GB/128GB or 8GB/256GB"
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Color *</label>
                  <input
                    type="text"
                    required
                    value={newVariant.color}
                    onChange={(e) => setNewVariant((curr) => ({ ...curr, color: e.target.value }))}
                    placeholder="e.g. Black, Blue, Gold"
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Sales Grade *</label>
                    <select
                      value={newVariant.sales_grade}
                      onChange={(e) => setNewVariant((curr) => ({ ...curr, sales_grade: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
                    >
                      {['OKAY', 'FAIR', 'GOOD', 'SUPERB', 'EXCELLENT'].map((g) => (
                        <option key={g} value={g}>{g}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Stock Quantity (Units) *</label>
                    <input
                      type="number"
                      min="0"
                      required
                      value={newVariant.stock_quantity}
                      onChange={(e) => setNewVariant((curr) => ({ ...curr, stock_quantity: e.target.value }))}
                      placeholder="e.g. 10"
                      className="w-full border rounded-lg px-3 py-2 text-sm font-bold text-indigo-900"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Selling Rate (₹)</label>
                    <input
                      type="number"
                      min="0"
                      value={newVariant.sale_price}
                      onChange={(e) => setNewVariant((curr) => ({ ...curr, sale_price: e.target.value }))}
                      placeholder={`e.g. ${product.mrp || 15000}`}
                      className="w-full border rounded-lg px-3 py-2 text-sm font-bold text-green-700"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Catalog MRP (₹)</label>
                    <input
                      type="number"
                      min="0"
                      value={newVariant.mrp}
                      onChange={(e) => setNewVariant((curr) => ({ ...curr, mrp: e.target.value }))}
                      placeholder={`e.g. ${product.mrp || 25000}`}
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-2 border-t">
                  <button
                    type="button"
                    onClick={() => setShowAddVariantModal(false)}
                    className="px-4 py-2 border rounded-lg text-sm text-gray-600 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={addingVariant}
                    className="px-4 py-2 bg-[#2b3380] hover:bg-[#1e2669] text-white rounded-lg text-sm font-semibold shadow disabled:opacity-50"
                  >
                    {addingVariant ? 'Saving...' : 'Save Variant & Stock'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ProductDetail;
